/**
 * Один сокет ленты на вкладку: дверь поднимает его, не дожидаясь /v1/me.
 *
 * Cookie уже на запросе. Ждать ответ двери — лишний RTT до handshake, хотя
 * доступ сервер и так решит сам (полный кадр / демо / пусто).
 *
 * Первый кадр сокета при тяжёлом такте едет секунды: `_pump_frames` ждёт
 * конец круга. Раньше через 3.5 с клиент рвал ещё живой сокет — «собираем»
 * крутилось, пока ядро отдавало тот же снимок заново. REST `/v1/signals`
 * читает HOT сразу и один раз — так страница и задумана (сторож, app.py).
 */

import { apiUrl, feedSocketUrl } from '../lib/build';
import { FEED_KIND } from '../lib/feedContext';
import { alignFork } from '../lib/align';
import type { Fork } from '../lib/protocol';
import { FeedClient, type FeedStatus } from '../ws/client';
import { bootReady } from './boot';
import { pace } from './calm';
import { readLastFrame, saveLastFrame } from './lastFrame';
import { lenta5Snapshot, onLenta5Publish, startLenta5Feed, stopLenta5Feed } from './lenta5Feed';
import { useScreen } from './store';
import { warmPinnacleDoors } from './hosts';

const PROTO = Number(import.meta.env.VITE_PROTO_VERSION ?? 1);
const FRAME_MS = 60;
/** Пока сокет открыт, столько ждём кадр, прежде чем снова смотреть. */
export const FRAME_WAIT_MS = 8_000;
/** REST-бустрап не имеет права держать «собираем» минуту: при клине такта fetch без срока висит. */
export const SIGNALS_MS = 4_000;
/** Открытый сокет без кадра дольше этого — тогда уже retry. */
export const FRAME_STUCK_MS = 20_000;

let client: FeedClient | null = null;
let ownedByGate = false;
let watched: string[] = [];
/** Старый сокет после смены ленты ещё может прислать кадр. Его не рисуем. */
let generation = 0;

/** Подписка карточек на сокет. Переживает смену ленты: клиент создаётся заново. */
export function noteWatch(ids: readonly string[]): void {
  watched = [...ids];
  client?.watch(watched);
}
let restGen = 0;
let liveAt = 0;
let mainForks = new Map<string, Fork>();
const frames = pace<{ forks: Map<string, Fork>; cut: number }>(FRAME_MS, ({ forks, cut }) =>
  useScreen.getState().apply(forks, cut),
);

function withLenta5(main: Map<string, Fork>): Map<string, Fork> {
  const out = new Map(main);
  for (const [id, fork] of lenta5Snapshot()) out.set(id, fork);
  return out;
}

function pinnacleUrlsOf(forks: Iterable<Fork>): string[] {
  const urls: string[] = [];
  for (const fork of forks) {
    for (const leg of fork.legs ?? []) {
      if (leg.s !== 'pinnacle') continue;
      if (typeof leg.url === 'string') urls.push(leg.url);
      if (typeof leg.betUrl === 'string') urls.push(leg.betUrl);
    }
  }
  return urls;
}

function applyState(forks: Map<string, Fork>, meta: Record<string, unknown>): void {
  mainForks = forks;
  const proto = typeof meta.proto === 'number' ? meta.proto : 0;
  const merged = withLenta5(forks);
  frames.push({ forks: merged, cut: typeof meta.truncated === 'number' ? meta.truncated : 0 });
  useScreen.getState().noteFunnel(meta.funnel);
  useScreen.getState().noteTick(meta);
  useScreen.getState().noteProto(proto);
  if (meta.snap === true) saveLastFrame(forks.values(), proto);
  warmPinnacleDoors(pinnacleUrlsOf(merged.values()));
  bootReady();
}

function applyLenta5(): void {
  const merged = withLenta5(mainForks);
  if (merged.size === 0 && !useScreen.getState().framed) return;
  frames.push({ forks: merged, cut: 0 });
  warmPinnacleDoors(pinnacleUrlsOf(merged.values()));
  bootReady();
}

function applyStatus(status: FeedStatus): void {
  useScreen.getState().setStatus(status);
  if (status === 'live') {
    liveAt = Date.now();
    armFrameWatch();
    return;
  }
  // Сокет не открылся или снова упал — REST читает HOT без очереди кадра.
  if ((status === 'connecting' || status === 'reconnecting') && !useScreen.getState().framed) {
    void hydrateSignals();
  }
}

let frameWatch = 0;
function armFrameWatch(): void {
  window.clearTimeout(frameWatch);
  frameWatch = window.setTimeout(() => {
    if (useScreen.getState().framed) return;
    // Открытый сокет молчит, потому что такт ещё сериализует кадр.
    // Рвать его — снова встать в хвост очереди `_pump_frames`.
    if (client?.alive() && Date.now() - liveAt < FRAME_STUCK_MS) {
      armFrameWatch();
      return;
    }
    client?.retry();
  }, FRAME_WAIT_MS);
}

export function hydrateLastFrame(): boolean {
  if (useScreen.getState().framed) {
    bootReady();
    return true;
  }
  const cached = readLastFrame();
  if (cached === null) return false;
  const restored = new Map(cached.forks.map((fork) => [fork.id, fork]));
  useScreen.getState().apply(restored);
  useScreen.getState().noteProto(cached.proto);
  warmPinnacleDoors(pinnacleUrlsOf(restored.values()));
  bootReady();
  return true;
}

/**
 * Один REST-снимок, пока сокет ещё собирает первый `t:'s'`.
 * Пустой ответ не ставит framed: это не «рынок спокоен», а ещё нет кадра.
 */
export async function hydrateSignals(): Promise<boolean> {
  if (useScreen.getState().framed) {
    bootReady();
    return true;
  }
  const gen = restGen;
  try {
    const answer = await fetch(apiUrl('/v1/signals'), {
      credentials: 'same-origin',
      signal: AbortSignal.timeout(SIGNALS_MS),
    });
    if (gen !== restGen) return false;
    if (!answer.ok) return false;
    const body = (await answer.json()) as {
      forks?: Fork[];
      proto?: number;
      truncated?: number;
    };
    if (gen !== restGen) return false;
    if (useScreen.getState().framed) return true;
    if (!Array.isArray(body.forks) || body.forks.length === 0) return false;
    const now = Date.now();
    const forks = new Map(
      body.forks.map((fork) => [fork.id, { ...alignFork(fork), at: now }]),
    );
    const proto = typeof body.proto === 'number' ? body.proto : 0;
    mainForks = forks;
    useScreen.getState().apply(
      withLenta5(forks),
      typeof body.truncated === 'number' ? body.truncated : 0,
    );
    useScreen.getState().noteProto(proto);
    saveLastFrame(forks.values(), proto);
    warmPinnacleDoors(pinnacleUrlsOf(forks.values()));
    bootReady();
    return true;
  } catch {
    return false;
  }
}

export function liveFeed(): FeedClient {
  if (client !== null) return client;
  const gen = generation;
  const current = (): boolean => gen === generation;
  client = new FeedClient(feedSocketUrl(location), PROTO, {
    onStatus: (status) => {
      if (current()) applyStatus(status);
    },
    onState: (forks, meta) => {
      if (current()) applyState(forks, meta);
    },
    onGone: (id, why) => {
      if (current()) useScreen.getState().noteGone(id, why);
    },
    onSeatRevoked: () => {
      if (current()) useScreen.getState().setStatus('revoked');
    },
    onProtocolMismatch: (serverProto) => {
      if (!current()) return;
      console.warn(`протокол сервера ${serverProto}, у нас ${PROTO} — перезагрузка`);
      location.reload();
    },
  });
  client.connect();
  client.watch(watched);
  if (FEED_KIND === 'main') {
    startLenta5Feed();
    onLenta5Publish(applyLenta5);
  }
  void hydrateSignals();
  return client;
}

export function primeLiveFeed(): void {
  hydrateLastFrame();
  ownedByGate = true;
  liveFeed();
}

export function stopLiveFeed(force = false): void {
  if (ownedByGate && !force) return;
  restGen += 1;
  window.clearTimeout(frameWatch);
  frames.stop();
  client?.close();
  client = null;
  stopLenta5Feed();
  mainForks = new Map();
  ownedByGate = false;
}

/** Закрыть сокет и открыть его уже для текущего источника. Кадр старой ленты не остаётся. */
export function retargetLiveFeed(): void {
  generation += 1;
  restGen += 1;
  window.clearTimeout(frameWatch);
  frames.stop();
  client?.close();
  client = null;
  stopLenta5Feed();
  mainForks = new Map();
  useScreen.setState({
    forks: new Map(),
    framed: false,
    ghosts: new Map(),
    bornAt: new Map(),
    open: null,
    sel: null,
    frozen: false,
    order: [],
    status: 'connecting',
    frameAt: null,
  });
  hydrateLastFrame();
  liveFeed();
}

export function releaseGateFeed(): void {
  ownedByGate = false;
  stopLiveFeed(true);
}

export function holdLiveFeed(): void {
  frames.hold();
}

export function wakeLiveFeed(): boolean {
  return frames.wake();
}
